import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { passwordStrength } from 'check-password-strength';
import { Form, Button, Input } from 'antd';

import { BigInput, BigPWDInput } from '@src/components/form';
import { PWD_REG } from '@src/consts/regex';
import messageCom from '@src/utils/messageCom';

import { getForgotPasswordEmailCode, forgotPasswordReset } from '@src/services/loginUser';
import styles from './styles.module.less';

const ForgotPWDForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const verifyCodeIntervalRef = useRef(null);

  const [pwdStrength, setPwdStrength] = useState('Checking...');
  const [getCodeLoading, setGetCodeLoading] = useState();
  const [verifyCodeTime, setVerifyCodeTime] = useState(60);
  const [verifyCodeBtnLoading, setVerifyCodeBtnLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  React.useEffect(() => {
    setPwdStrength('Checking...');

    return () => {
      clearInterval(verifyCodeIntervalRef.current)
    }
  }, [])

  const onFinish = async (data) => {
    setSubmitLoading(true);

    const res = await forgotPasswordReset({
      email: data?.email,
      password: data?.password,
      code: data?.emailCode
    });

    setSubmitLoading(false);
    if (!res) return;

    messageCom.success(t('Password reset successful!'));
    navigate('/login');
  };

  const onGetCode = async () => {
    let email = formRef.current?.getFieldValue('email');
    if (!email) return;

    setGetCodeLoading(true);

    const res = await getForgotPasswordEmailCode({
      email
    });

    if (!res) return;

    messageCom.success(t("Successfully obtained the verification code, please check it carefully!"));
    setVerifyCodeBtnLoading(true);


    verifyCodeIntervalRef.current = setInterval(() => {
      setVerifyCodeTime((oldVal) => {
        if (oldVal > 1) return oldVal - 1;

        clearInterval(verifyCodeIntervalRef.current);
        setVerifyCodeBtnLoading(false);

        return 60;
      })
    }, 1000);
    setGetCodeLoading(false);
  }

  return (
    <div className={styles.forgot_pwd_form}>
      <h1>{t("Forgot Password")}</h1>
      <p>{t("Insert email address to get verification code and reset password")}</p>
      <div className={styles.form_box}>
        <Form
          layout='vertical'
          ref={formRef}
          onFinish={onFinish}
        >
          <Form.Item>
            <Input.Group className={styles.email_box}>
              <Form.Item
                name="email"
                label={t("Email")}
                rules={[
                  {
                    required: true,
                    message: t('Please enter your Email')
                  },
                  {
                    type: 'email',
                    message: t('Please enter the correct E-mail format')
                  }
                ]}
              >
                <BigInput
                  name="email"
                  placeholder={t("Please enter your Email")}
                  style={{
                    flex: 1,
                    borderRadius: '6px 0 0 6px'
                  }}
                />
              </Form.Item>
              <Button
                type="primary"
                onClick={() => {
                  formRef.current.validateFields(['email'])
                  onGetCode();
                }}
                loading={getCodeLoading}
                disabled={verifyCodeBtnLoading}
                style={{
                  height: '.45rem',
                  minHeight: 30
                }}
              >
                {
                  verifyCodeBtnLoading
                    ? `${verifyCodeTime} s`
                    : t("Get Verifcation Code")
                }
              </Button>
            </Input.Group>
          </Form.Item>
          <Form.Item
            label={t("Verification Code")}
            name="emailCode"
            rules={[
              {
                required: true,
                message: t("Please enter your Verification Code")
              }
            ]}
          >
            <BigInput name="emailCode" placeholder={t("Please enter your Verification Code")} />
          </Form.Item>
          <div className={styles.pwd_box}>
            <Form.Item
              name="password"
              label={t("Password")}
              required
              rules={[
                () => ({
                  validator(_, value) {
                    if (!value) {
                      setPwdStrength("Checking...");

                      return Promise.reject(t("Please enter your Password"));
                    }

                    setPwdStrength(passwordStrength(value).value);
                    if (!PWD_REG.test(value)) {
                      return Promise.reject(t("Please enter the password in the correct format"));
                    }

                    return Promise.resolve();
                  }
                })
              ]}
            >
              <BigPWDInput name="password" placeholder={t("Please enter your Password")} />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label={t("Confirm Password")}
              dependencies={['password']}
              required
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(t('Please confirm your password'))
                    }
                    if (getFieldValue().password !== value) {
                      return Promise.reject(t("The passwords do not match!"))
                    }

                    return Promise.resolve()
                  }
                })
              ]}
            >
              <BigPWDInput name="confirmPassword" placeholder={t("Please confirm your password")} />
            </Form.Item>
          </div>
          <div className={styles.pwdStrength}>
            <span>{t("Password Strength：")}</span>
            <span className={styles.strength}>{t(pwdStrength)}</span>
            <p>{t("Your password must be at least 8 characters long, have a mixture of uppercase and lowercase letters, at least 1 number, at least 1 symbol and achieve a Password Strength of 'Strong'")}</p>
          </div>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.submit_btn}
            loading={submitLoading}
          >
            {t("Reset Password")}
          </Button>
        </Form>
        <p className={styles.footer}>
          <Button type="link" onClick={() => navigate('/login')}>{t("Back to login")}</Button>
        </p>
      </div>
    </div>
  )
};

export default ForgotPWDForm;